import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { Logo } from '@/components/ui/logo';

// ─── Shared: Seeded PRNG (mulberry32) ─────────────────────────────────────────
function createPRNG(initialSeed: number) {
  let seed = initialSeed;
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateEquityData(): number[] {
  const rand = createPRNG(42);
  const pts = 120;
  const data: number[] = [];
  let eq = 10000;
  for (let i = 0; i <= pts; i++) {
    if (i < 48) eq += 18 + rand() * 38;
    else if (i < 66) eq -= 12 + rand() * 45;
    else if (i < 80) eq += 22 + rand() * 42;
    else eq += 16 + rand() * 32;
    data.push(eq);
  }
  data[data.length - 1] = 13016;
  return data;
}

// ─── Panel A: Animated Stats + Equity Curve ───────────────────────────────────
function PanelA() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dataRef = useRef<number[]>([]);
  const animRef = useRef(0);
  const doneRef = useRef(false);

  const drawFrame = useCallback((upTo: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const w = rect.width,
      h = rect.height;
    const data = dataRef.current;
    const pts = data.length - 1;
    const min = Math.min(...data) * 0.998;
    const max = Math.max(...data) * 1.002;
    const range = max - min;
    const toX = (i: number) => (i / pts) * w;
    const toY = (v: number) => h - ((v - min) / range) * (h - 16) - 8;

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const y = 8 + (i / 3) * (h - 16);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    if (upTo < 1) return;
    const end = Math.min(upTo, pts);

    // Fill
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, 'rgba(52,211,153,0.12)');
    grad.addColorStop(1, 'rgba(52,211,153,0.0)');
    ctx.beginPath();
    ctx.moveTo(toX(0), h);
    for (let i = 0; i <= end; i++) ctx.lineTo(toX(i), toY(data[i]!));
    ctx.lineTo(toX(end), h);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Line
    ctx.beginPath();
    ctx.moveTo(toX(0), toY(data[0]!));
    for (let i = 1; i <= end; i++) ctx.lineTo(toX(i), toY(data[i]!));
    ctx.strokeStyle = 'rgba(52,211,153,0.7)';
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Dot
    const lx = toX(end),
      ly = toY(data[end]!);
    ctx.beginPath();
    ctx.arc(lx, ly, 3, 0, Math.PI * 2);
    ctx.fillStyle = '#34d399';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(lx, ly, 6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(52,211,153,0.2)';
    ctx.fill();
  }, []);

  useEffect(() => {
    dataRef.current = generateEquityData();
    const pts = dataRef.current.length - 1;
    let progress = 0;
    const speed = 2.5;
    const tick = () => {
      progress += speed;
      const show = Math.min(Math.floor(progress), pts);
      drawFrame(show);
      if (show < pts) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        doneRef.current = true;
      }
    };
    animRef.current = requestAnimationFrame(tick);

    const handleResize = () => {
      if (doneRef.current) drawFrame(dataRef.current.length - 1);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [drawFrame]);

  return (
    <div className="flex flex-col items-center justify-center h-full px-10 py-12 gap-8">
      {/* Equity curve */}
      <div className="w-full max-w-lg">
        <div
          className="rounded-xl overflow-hidden"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid var(--color-border)',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: 180, display: 'block' }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-6 text-center">
        {[
          { value: '4,139+', label: 'Trades Analyzed' },
          { value: '24/7', label: 'Autopilot' },
          { value: '< 1s', label: 'Execution' },
        ].map((s) => (
          <div key={s.label}>
            <div className="text-xl font-extrabold font-mono text-orange">
              {s.value}
            </div>
            <div className="text-[11px] text-ink-3 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quote */}
      <p className="text-sm text-ink-2 text-center italic max-w-md">
        &ldquo;Upload your history. Get a strategy in 60 seconds.&rdquo;
      </p>

      {/* Trust */}
      <div className="flex items-center gap-6 text-[11px] text-ink-3">
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          No password stored
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Bank-grade encryption
        </span>
      </div>
    </div>
  );
}

// ─── Panel B: 3-Step Flow ─────────────────────────────────────────────────────
function PanelB() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  const steps = [
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="20"
          height="20"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      ),
      title: 'Upload History',
      desc: 'Drop your MT5 trade export',
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="20"
          height="20"
        >
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      ),
      title: 'Forge Strategy',
      desc: 'AI extracts winning patterns',
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="20"
          height="20"
        >
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 3 0 3 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-3 0-3" />
        </svg>
      ),
      title: 'Deploy Live',
      desc: 'One-click automated trading',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-10 py-12">
      <div className="relative flex flex-col gap-0 w-full max-w-sm">
        {/* Dashed connector line */}
        <div
          className="absolute left-[22px] top-[44px] bottom-[44px] w-px"
          style={{
            borderLeft: '2px dashed rgba(249,115,22,0.25)',
          }}
        />

        {steps.map((step, i) => (
          <div
            key={step.title}
            className="flex items-start gap-5 relative"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: `opacity 0.5s ease ${i * 0.2}s, transform 0.5s ease ${i * 0.2}s`,
              paddingBottom: i < steps.length - 1 ? 36 : 0,
            }}
          >
            {/* Number circle */}
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 relative z-10 text-sm font-bold"
              style={{
                background:
                  'linear-gradient(135deg, rgba(249,115,22,0.2), rgba(251,146,60,0.1))',
                border: '1px solid rgba(249,115,22,0.3)',
                color: '#f97316',
              }}
            >
              {i + 1}
            </div>

            {/* Card */}
            <div
              className="flex-1 rounded-xl px-5 py-4"
              style={{
                background: 'var(--color-glass)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div className="flex items-center gap-2.5 mb-1.5">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: 'rgba(249,115,22,0.08)',
                    border: '1px solid rgba(249,115,22,0.15)',
                    color: '#f97316',
                  }}
                >
                  {step.icon}
                </div>
                <span className="text-sm font-bold text-ink">{step.title}</span>
              </div>
              <p className="text-xs text-ink-3 leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tagline */}
      <p
        className="text-sm text-ink-2 text-center mt-10 font-medium"
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.5s ease 0.8s',
        }}
      >
        From history to live in under 2 minutes
      </p>
    </div>
  );
}

// ─── Panel C: Rotating Feature Cards ──────────────────────────────────────────
function PanelC() {
  const [active, setActive] = useState(0);

  const features = [
    {
      title: 'Grid Detection',
      desc: 'Automatically detects your grid spacing, direction bias, and basket close patterns from raw trade history.',
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="#f97316"
          strokeWidth="2"
          width="22"
          height="22"
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
        </svg>
      ),
      color: '#f97316',
      bg: 'rgba(249,115,22,0.08)',
      border: 'rgba(249,115,22,0.15)',
    },
    {
      title: 'The Banker',
      desc: 'Auto-skim profits to your secure wallet when targets hit. Auto-rescue bots when equity drops. Capital protection that never sleeps.',
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="#34d399"
          strokeWidth="2"
          width="22"
          height="22"
        >
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      color: '#34d399',
      bg: 'rgba(52,211,153,0.08)',
      border: 'rgba(52,211,153,0.15)',
    },
    {
      title: 'Backtest First',
      desc: 'Run your strategy through months of historical data. See exact equity curves, drawdowns, and friction costs before risking a dollar.',
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="#60a5fa"
          strokeWidth="2"
          width="22"
          height="22"
        >
          <path d="M3 3v18h18" />
          <path d="M7 16l4-8 4 4 6-6" />
        </svg>
      ),
      color: '#60a5fa',
      bg: 'rgba(96,165,250,0.08)',
      border: 'rgba(96,165,250,0.15)',
    },
    {
      title: '50+ Positions',
      desc: 'Handle massive grid strategies with virtual capital isolation. Each bot gets its own equity — no cross-contamination.',
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="#a78bfa"
          strokeWidth="2"
          width="22"
          height="22"
        >
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      ),
      color: '#a78bfa',
      bg: 'rgba(167,139,250,0.08)',
      border: 'rgba(167,139,250,0.15)',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [features.length]);

  const f = features[active]!;

  return (
    <div className="flex flex-col items-center justify-center h-full px-10 py-12 gap-8">
      {/* Rotating card */}
      <div className="w-full max-w-sm relative" style={{ minHeight: 180 }}>
        <div
          key={active}
          className="rounded-xl px-6 py-6"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid var(--color-border)',
            animation: 'demoFadeIn 0.4s ease both',
          }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
            style={{
              background: f.bg,
              border: `1px solid ${f.border}`,
            }}
          >
            {f.icon}
          </div>
          <h3 className="text-base font-bold mb-2" style={{ color: f.color }}>
            {f.title}
          </h3>
          <p className="text-xs text-ink-3 leading-relaxed">{f.desc}</p>
        </div>
      </div>

      {/* Dots */}
      <div className="flex gap-2">
        {features.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className="w-2 h-2 rounded-full transition-all duration-300"
            style={{
              background:
                i === active
                  ? '#f97316'
                  : 'var(--color-border-2)',
              transform: i === active ? 'scale(1.3)' : 'scale(1)',
            }}
          />
        ))}
      </div>

      {/* Stat strip */}
      <div className="flex gap-4 text-[11px] text-ink-3 font-medium">
        <span>5 Strategies</span>
        <span style={{ color: 'var(--color-border-2)' }}>|</span>
        <span>24/7 Execution</span>
        <span style={{ color: 'var(--color-border-2)' }}>|</span>
        <span>Zero Passwords</span>
      </div>
    </div>
  );
}

// ─── Panel D: Dashboard Preview ───────────────────────────────────────────────
function PanelD() {
  // Static SVG equity curve path
  const curvePath =
    'M0,60 C20,55 40,45 60,42 C80,39 100,50 120,48 C140,46 160,35 180,30 C200,25 220,28 240,22 C260,16 280,12 300,8';

  return (
    <div className="flex flex-col items-center justify-center h-full px-10 py-12 gap-6">
      {/* Mock dashboard */}
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: 'var(--color-glass)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
        }}
      >
        {/* Top bar */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, rgba(249,115,22,0.3), rgba(251,146,60,0.15))',
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <span className="text-xs font-bold text-ink">Portfolio</span>
            <span className="text-sm font-extrabold font-mono text-green">
              $12,450
            </span>
          </div>
          <div className="text-[10px] text-ink-3">
            Active Bots:{' '}
            <span className="text-ink font-bold">3</span>
          </div>
        </div>

        {/* Mini curve */}
        <div className="px-5 py-3">
          <svg
            viewBox="0 0 300 68"
            className="w-full"
            style={{ height: 60 }}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient
                id="demoGrad"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor="rgba(52,211,153,0.12)"
                />
                <stop
                  offset="100%"
                  stopColor="rgba(52,211,153,0)"
                />
              </linearGradient>
            </defs>
            <path
              d={curvePath + ' L300,68 L0,68 Z'}
              fill="url(#demoGrad)"
            />
            <path
              d={curvePath}
              fill="none"
              stroke="rgba(52,211,153,0.6)"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        {/* Bot cards */}
        <div className="px-5 pb-4 space-y-2">
          {[
            {
              name: 'Gold Grid v3',
              symbol: 'XAUUSD',
              pnl: '+$234.50',
              status: 'Running',
            },
            {
              name: 'EUR Scalper',
              symbol: 'EURUSD',
              pnl: '+$89.20',
              status: 'Running',
            },
            {
              name: 'Volatility 75',
              symbol: 'V75',
              pnl: '+$156.80',
              status: 'Running',
            },
          ].map((bot) => (
            <div
              key={bot.name}
              className="flex items-center justify-between px-3.5 py-2.5 rounded-lg"
              style={{
                background: 'var(--color-glass)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div>
                <div className="text-xs font-bold text-ink">
                  {bot.name}
                </div>
                <div className="text-[10px] text-ink-4">{bot.symbol}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold font-mono text-green">
                  {bot.pnl}
                </span>
                <span
                  className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(52,211,153,0.1)',
                    color: '#34d399',
                    border: '1px solid rgba(52,211,153,0.15)',
                  }}
                >
                  {bot.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stat pills */}
      <div className="flex flex-wrap justify-center gap-2.5">
        {[
          { label: 'Win Rate', value: '68%', color: '#34d399' },
          { label: 'Profit Factor', value: '2.1', color: '#f97316' },
          { label: 'Max DD', value: '4.2%', color: '#f87171' },
        ].map((p) => (
          <span
            key={p.label}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full"
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-glass-border)',
            }}
          >
            <span style={{ color: p.color }} className="font-mono">
              {p.value}
            </span>
            <span className="text-ink-3">{p.label}</span>
          </span>
        ))}
      </div>

      {/* Tagline */}
      <p className="text-sm text-ink-2 font-medium">Your edge, automated</p>
    </div>
  );
}

// ─── Password rules (shared with signup tab) ─────────────────────────────────
const passwordRules = [
  { label: '8+ chars', test: (pw: string) => pw.length >= 8 },
  { label: 'Uppercase', test: (pw: string) => /[A-Z]/.test(pw) },
  { label: 'Lowercase', test: (pw: string) => /[a-z]/.test(pw) },
  { label: 'Number', test: (pw: string) => /\d/.test(pw) },
];

// ─── Mock Login Form (right panel) ────────────────────────────────────────────
function MockLoginForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [signupPassword, setSignupPassword] = useState('');

  const ruleResults = useMemo(
    () => passwordRules.map((r) => ({ ...r, passed: r.test(signupPassword) })),
    [signupPassword],
  );

  return (
    <div className="w-full max-w-sm">
      {/* Logo */}
      <div className="flex items-center mb-10 justify-center">
        <Logo size="lg" />
      </div>

      <div>
        {/* Tab toggle */}
        <div
          className="flex rounded-lg p-1 mb-6"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
          }}
        >
          {(['login', 'signup'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTab(t); setShowPassword(false); setSignupPassword(''); }}
              className="flex-1 text-xs font-bold py-2 rounded-md transition-all duration-200"
              style={{
                background: tab === t ? 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(245,158,11,0.08))' : 'transparent',
                color: tab === t ? '#f97316' : 'var(--color-ink-3)',
                border: tab === t ? '1px solid rgba(249,115,22,0.2)' : '1px solid transparent',
              }}
            >
              {t === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>

        <h1 className="text-lg font-extrabold mb-1">
          {tab === 'login' ? 'Welcome back' : 'Create Account'}
        </h1>
        <p className="text-xs text-ink-3 mb-1.5">
          {tab === 'login' ? 'Sign in to your ForgeAlpha account' : 'Start building your trading strategies'}
        </p>

        {/* Gradient divider */}
        <div
          className="h-px mb-6"
          style={{
            background: 'linear-gradient(to right, transparent, rgba(249,115,22,0.2), transparent)',
          }}
        />

        <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
          {/* Email with icon */}
          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">
              Email
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full pl-9 pr-3 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
              />
            </div>
          </div>

          {/* Password with icon + eye toggle */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[11px] font-semibold text-ink-3">
                Password
              </label>
              {tab === 'login' && (
                <button type="button" tabIndex={-1} className="text-[10px] text-orange font-semibold hover:underline">
                  Forgot password?
                </button>
              )}
            </div>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={tab === 'login' ? 'Enter password' : 'Create a strong password'}
                value={tab === 'signup' ? signupPassword : undefined}
                onChange={tab === 'signup' ? (e) => setSignupPassword(e.target.value) : undefined}
                className="w-full pl-9 pr-10 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-4 hover:text-ink-3 transition-colors"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* Password rules (signup only) */}
          {tab === 'signup' && (
            <div className="flex flex-wrap gap-2">
              {ruleResults.map((r) => (
                <span
                  key={r.label}
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md transition-colors ${
                    r.passed ? 'bg-green-bg text-green' : 'bg-surface text-ink-4'
                  }`}
                >
                  {r.passed ? <Check size={10} /> : <X size={10} />}
                  {r.label}
                </span>
              ))}
            </div>
          )}

          {/* Referral code (signup only) */}
          {tab === 'signup' && (
            <div>
              <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">
                Referral Code <span className="text-ink-4 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Enter referral code"
                  className="w-full pl-9 pr-3 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
                />
              </div>
            </div>
          )}

          {/* Terms & conditions (signup only) */}
          {tab === 'signup' && (
            <label className="flex items-start gap-2 cursor-pointer group">
              <div
                className="w-4 h-4 rounded flex items-center justify-center transition-all duration-200 mt-0.5 shrink-0"
                style={{
                  background: acceptTerms ? 'linear-gradient(135deg, #f97316, #f59e0b)' : 'var(--color-surface)',
                  border: acceptTerms ? '1px solid rgba(249,115,22,0.4)' : '1px solid var(--color-border-2)',
                }}
                onClick={() => setAcceptTerms((v) => !v)}
              >
                {acceptTerms && <Check size={10} className="text-white" />}
              </div>
              <span className="text-[11px] text-ink-3 group-hover:text-ink-2 transition-colors select-none leading-relaxed">
                I agree to the{' '}
                <button type="button" className="text-orange font-semibold hover:underline">Terms of Service</button>
                {' '}and{' '}
                <button type="button" className="text-orange font-semibold hover:underline">Privacy Policy</button>
              </span>
            </label>
          )}

          {/* Remember me (login only) */}
          {tab === 'login' && (
            <label className="flex items-center gap-2 cursor-pointer group">
              <div
                className="w-4 h-4 rounded flex items-center justify-center transition-all duration-200"
                style={{
                  background: remember ? 'linear-gradient(135deg, #f97316, #f59e0b)' : 'var(--color-surface)',
                  border: remember ? '1px solid rgba(249,115,22,0.4)' : '1px solid var(--color-border-2)',
                }}
                onClick={() => setRemember((v) => !v)}
              >
                {remember && <Check size={10} className="text-white" />}
              </div>
              <span className="text-[11px] text-ink-3 group-hover:text-ink-2 transition-colors select-none">
                Remember me
              </span>
            </label>
          )}

          {/* CTA button */}
          <button
            type="button"
            className="w-full py-3 rounded-lg text-sm font-bold text-white transition-all hover:brightness-110 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #f97316, #f59e0b)',
              boxShadow: '0 0 20px rgba(249,115,22,0.25)',
            }}
          >
            {tab === 'login' ? 'Sign In' : 'Create Account'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </form>

        {/* Trust note */}
        <p className="text-[10px] text-ink-4 text-center mt-3">
          {tab === 'login' ? 'No credit card required' : 'Free to start — no credit card required'}
        </p>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
          <span className="text-[10px] text-ink-4 font-medium">or continue with</span>
          <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
        </div>

        {/* Social buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold text-ink-3 transition-all hover:bg-glass-2 hover:text-ink"
            style={{
              background: 'var(--color-glass)',
              border: '1px solid var(--color-glass-border)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold text-ink-3 transition-all hover:bg-glass-2 hover:text-ink"
            style={{
              background: 'var(--color-glass)',
              border: '1px solid var(--color-glass-border)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Apple
          </button>
        </div>

        {/* Toggle link */}
        <p className="text-xs text-ink-3 text-center mt-6">
          {tab === 'login' ? (
            <>
              Don&apos;t have an account?{' '}
              <button type="button" onClick={() => setTab('signup')} className="text-orange font-semibold hover:underline">
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => setTab('login')} className="text-orange font-semibold hover:underline">
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

// ─── Shared Split Layout ──────────────────────────────────────────────────────
const panels: Record<string, React.FC> = {
  A: PanelA,
  B: PanelB,
  C: PanelC,
  D: PanelD,
};

export default function DemoAuthPage({
  variant,
}: {
  variant: 'A' | 'B' | 'C' | 'D';
}) {
  const Panel = panels[variant]!;

  return (
    <>
      {/* Keyframes for Panel C fade */}
      <style>{`
        @keyframes demoFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Left promotional panel — hidden on mobile */}
        <div
          className="hidden lg:flex relative overflow-hidden"
          style={{
            flex: '0 0 57%',
            background:
              'linear-gradient(135deg, var(--color-bg), var(--color-bg-2))',
          }}
        >
          {/* Orange glow overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(249,115,22,0.06), transparent 70%)',
            }}
          />
          {/* Glass border on right */}
          <div
            className="absolute right-0 top-0 bottom-0 w-px"
            style={{
              background:
                'linear-gradient(to bottom, transparent, rgba(249,115,22,0.15), transparent)',
            }}
          />
          {/* Panel content */}
          <div className="relative z-10 w-full">
            <Panel />
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex-1 flex items-start justify-center p-6 lg:p-12 overflow-y-auto" style={{ paddingTop: 'max(2rem, calc(50vh - 320px))' }}>
          <MockLoginForm />
        </div>
      </div>
    </>
  );
}
